import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, Upload, Loader2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, publicAssetUrl, errorMessage } from "@/lib/api";

const slugify = (s) =>
    (s || "")
        .toLowerCase()
        .replace(/&/g, " and ")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

const districtSlugOf = (name) => (name || "").toLowerCase().replace(/\s+/g, "-");

const EMPTY_BRAND = () => ({
    brand_name: "",
    slug: "",
    category_slug: "supermarkets",
    tagline: "",
    website_url: "",
    is_featured: false,
    branches: [],
    logo_url: null,
});

const EMPTY_BRANCH = (districtNames) => ({
    district_slug: districtSlugOf(districtNames[0]),
    address: "",
    contact_number: "",
    google_maps_url: "",
    hours: "",
});

export default function BrandEditorDrawer({
    open,
    shop, // null = create; object = edit
    categorySlugs,
    districtNames,
    onClose,
    onSaved,
}) {
    const isEdit = !!shop?.id;
    const [form, setForm] = useState(EMPTY_BRAND());
    const [saving, setSaving] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [slugDirty, setSlugDirty] = useState(false);

    // Reset form each time the drawer opens for a different shop / create.
    useEffect(() => {
        if (!open) return;
        if (shop) {
            setForm({
                brand_name: shop.brand_name || "",
                slug: shop.slug || "",
                category_slug: shop.category_slug || categorySlugs[0],
                tagline: shop.tagline || "",
                website_url: shop.website_url || "",
                is_featured: !!shop.is_featured,
                branches: (shop.branches || []).map((b) => ({ ...b })),
                logo_url: shop.logo_url || null,
                id: shop.id,
            });
            setSlugDirty(true); // don't auto-clobber an existing slug
        } else {
            setForm(EMPTY_BRAND());
            setSlugDirty(false);
        }
    }, [open, shop, categorySlugs]);

    // Auto-populate slug while typing brand_name in create mode.
    useEffect(() => {
        if (isEdit || slugDirty) return;
        setForm((f) => ({ ...f, slug: slugify(f.brand_name) }));
    }, [form.brand_name, isEdit, slugDirty]);

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const setBranchField = (idx, k, v) => {
        setForm((f) => ({
            ...f,
            branches: f.branches.map((b, i) => (i === idx ? { ...b, [k]: v } : b)),
        }));
    };
    const addBranch = () =>
        setForm((f) => ({ ...f, branches: [...f.branches, EMPTY_BRANCH(districtNames)] }));
    const removeBranch = (idx) =>
        setForm((f) => ({ ...f, branches: f.branches.filter((_, i) => i !== idx) }));

    const canSave = useMemo(() => {
        if (!form.brand_name.trim() || !form.slug.trim() || !form.category_slug) return false;
        for (const b of form.branches) {
            if (!b.address?.trim() || !b.district_slug) return false;
        }
        return true;
    }, [form]);

    const onLogoFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // let the same file be picked again later
        if (!file) return;
        if (!isEdit) {
            toast.error("Save the brand first, then upload a logo.");
            return;
        }
        setUploadingLogo(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const res = await api.post(`/admin/partner-shops/${form.id}/logo`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setForm((f) => ({ ...f, logo_url: res.data.logo_url }));
            toast.success("Logo updated");
        } catch (err) {
            toast.error(errorMessage(err));
        } finally {
            setUploadingLogo(false);
        }
    };

    const onSave = async () => {
        if (!canSave || saving) return;
        setSaving(true);
        try {
            const payload = {
                brand_name: form.brand_name.trim(),
                slug: form.slug.trim(),
                category_slug: form.category_slug,
                tagline: form.tagline?.trim() || null,
                website_url: form.website_url?.trim() || null,
                is_featured: !!form.is_featured,
                branches: form.branches.map((b) => ({
                    district_slug: b.district_slug,
                    address: b.address.trim(),
                    contact_number: b.contact_number?.trim() || null,
                    google_maps_url: b.google_maps_url?.trim() || null,
                    hours: b.hours?.trim() || null,
                })),
            };
            if (isEdit) {
                // Update the top-level brand fields.
                await api.patch(`/admin/partner-shops/${form.id}`, {
                    brand_name: payload.brand_name,
                    slug: payload.slug,
                    category_slug: payload.category_slug,
                    tagline: payload.tagline,
                    website_url: payload.website_url,
                    is_featured: payload.is_featured,
                });
                // Rebuild the branches list authoritatively by clearing and re-adding.
                // For simplicity we PATCH each existing branch or add/delete as needed.
                await syncBranches(form.id, shop.branches || [], payload.branches);
                toast.success("Brand updated");
            } else {
                await api.post("/admin/partner-shops", payload);
                toast.success("Brand created");
            }
            onSaved?.();
        } catch (err) {
            toast.error(errorMessage(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={(v) => (!v && !saving) && onClose?.()}>
            <SheetContent
                side="right"
                className="w-full sm:max-w-xl overflow-y-auto"
                data-testid="partner-shop-editor-drawer"
            >
                <SheetHeader className="text-left">
                    <SheetTitle className="font-heading text-2xl font-extrabold">
                        {isEdit ? "Edit brand" : "Add brand"}
                    </SheetTitle>
                    <SheetDescription className="font-body text-sm text-[var(--text-muted)]">
                        {isEdit
                            ? "Update brand details, branches and featured status."
                            : "Save the brand first, then upload a logo and add branches from the edit view."}
                    </SheetDescription>
                </SheetHeader>

                <div className="mt-6 space-y-6">
                    {/* Brand fields ------------------------------------------------ */}
                    <section className="space-y-4">
                        <div className="flex items-center gap-4">
                            <div className="flex flex-col items-center gap-2">
                                {form.logo_url ? (
                                    <img
                                        src={publicAssetUrl(form.logo_url)}
                                        alt="Logo"
                                        className="h-20 w-20 rounded-2xl bg-white object-contain ring-1 ring-slate-100"
                                        data-testid="partner-shop-editor-logo-preview"
                                    />
                                ) : (
                                    <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-[#FFF3E6] font-heading text-xs font-semibold text-[var(--brand-orange)]">
                                        No logo
                                    </div>
                                )}
                                <label
                                    className={`inline-flex cursor-pointer items-center gap-1 rounded-full border border-slate-200 px-3 py-1 font-heading text-xs font-semibold text-[var(--text-primary)] hover:bg-slate-50 ${!isEdit ? "opacity-50" : ""}`}
                                    data-testid="partner-shop-editor-logo-btn"
                                >
                                    {uploadingLogo ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                                    Upload
                                    <input
                                        type="file"
                                        accept="image/png,image/jpeg,image/webp,image/svg+xml"
                                        className="sr-only"
                                        onChange={onLogoFile}
                                        disabled={!isEdit || uploadingLogo}
                                    />
                                </label>
                            </div>
                            <div className="flex flex-1 items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
                                <div>
                                    <div className="font-heading text-sm font-semibold text-[var(--text-primary)]">
                                        Featured
                                    </div>
                                    <div className="font-body text-xs text-[var(--text-muted)]">
                                        Pin to the top of both customer surfaces.
                                    </div>
                                </div>
                                <Switch
                                    checked={form.is_featured}
                                    onCheckedChange={(v) => setField("is_featured", v)}
                                    data-testid="partner-shop-editor-featured-toggle"
                                    className="data-[state=checked]:bg-[var(--brand-orange)]"
                                />
                            </div>
                        </div>

                        <FormRow label="Brand name">
                            <Input
                                value={form.brand_name}
                                onChange={(e) => setField("brand_name", e.target.value)}
                                data-testid="partner-shop-editor-name"
                                placeholder="e.g. Nesto Hypermarket"
                            />
                        </FormRow>
                        <FormRow label="Slug" hint="Lowercase, hyphens only. Used in URLs.">
                            <Input
                                value={form.slug}
                                onChange={(e) => { setSlugDirty(true); setField("slug", e.target.value); }}
                                data-testid="partner-shop-editor-slug"
                                placeholder="nesto-hypermarket"
                            />
                        </FormRow>
                        <FormRow label="Category">
                            <select
                                value={form.category_slug}
                                onChange={(e) => setField("category_slug", e.target.value)}
                                data-testid="partner-shop-editor-category"
                                className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 font-body text-sm text-[var(--text-primary)] focus:border-[var(--brand-orange)] focus:outline-none focus:ring-1 focus:ring-[var(--brand-orange)]"
                            >
                                {categorySlugs.map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        </FormRow>
                        <FormRow label="Tagline">
                            <Input
                                value={form.tagline}
                                onChange={(e) => setField("tagline", e.target.value)}
                                data-testid="partner-shop-editor-tagline"
                                placeholder="Short brand tagline"
                            />
                        </FormRow>
                        <FormRow label="Website URL">
                            <Input
                                value={form.website_url}
                                onChange={(e) => setField("website_url", e.target.value)}
                                data-testid="partner-shop-editor-website"
                                placeholder="https://…"
                            />
                        </FormRow>
                    </section>

                    {/* Branches ---------------------------------------------------- */}
                    <section>
                        <div className="mb-2 flex items-center justify-between">
                            <div>
                                <h3 className="font-heading text-base font-bold text-[var(--text-primary)]">
                                    Branches
                                </h3>
                                <p className="font-body text-xs text-[var(--text-muted)]">
                                    Add one row per Kerala district you serve.
                                </p>
                            </div>
                            <Button
                                variant="outline"
                                onClick={addBranch}
                                className="rounded-xl border-slate-200"
                                data-testid="partner-shop-branch-add-btn"
                            >
                                <Plus size={14} className="mr-2" /> Add Branch
                            </Button>
                        </div>

                        {form.branches.length === 0 ? (
                            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center font-body text-sm text-[var(--text-muted)]">
                                No branches yet. Click <strong>Add Branch</strong> to create one.
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {form.branches.map((b, idx) => (
                                    <div
                                        key={idx}
                                        className="rounded-xl border border-slate-200 bg-white p-4"
                                        data-testid={`partner-shop-branch-row-${idx}`}
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="font-heading text-xs font-semibold uppercase tracking-wider text-[var(--brand-orange)]">
                                                Branch #{idx + 1}
                                            </div>
                                            <button
                                                onClick={() => removeBranch(idx)}
                                                className="text-slate-400 hover:text-rose-600"
                                                data-testid={`partner-shop-branch-remove-${idx}`}
                                                aria-label="Remove branch"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                        <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                            <FormRow label="District">
                                                <select
                                                    value={b.district_slug}
                                                    onChange={(e) => setBranchField(idx, "district_slug", e.target.value)}
                                                    data-testid={`partner-shop-branch-district-${idx}`}
                                                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 font-body text-sm text-[var(--text-primary)] focus:border-[var(--brand-orange)] focus:outline-none focus:ring-1 focus:ring-[var(--brand-orange)]"
                                                >
                                                    {districtNames.map((n) => (
                                                        <option key={n} value={districtSlugOf(n)}>{n}</option>
                                                    ))}
                                                </select>
                                            </FormRow>
                                            <FormRow label="Contact number">
                                                <Input
                                                    value={b.contact_number || ""}
                                                    onChange={(e) => setBranchField(idx, "contact_number", e.target.value)}
                                                    data-testid={`partner-shop-branch-contact-${idx}`}
                                                    placeholder="+91 …"
                                                />
                                            </FormRow>
                                            <FormRow label="Address" className="sm:col-span-2">
                                                <Input
                                                    value={b.address}
                                                    onChange={(e) => setBranchField(idx, "address", e.target.value)}
                                                    data-testid={`partner-shop-branch-address-${idx}`}
                                                    placeholder="Street, city, PIN"
                                                />
                                            </FormRow>
                                            <FormRow label="Google Maps URL">
                                                <Input
                                                    value={b.google_maps_url || ""}
                                                    onChange={(e) => setBranchField(idx, "google_maps_url", e.target.value)}
                                                    data-testid={`partner-shop-branch-maps-${idx}`}
                                                    placeholder="https://maps.google.com/…"
                                                />
                                            </FormRow>
                                            <FormRow label="Hours">
                                                <Input
                                                    value={b.hours || ""}
                                                    onChange={(e) => setBranchField(idx, "hours", e.target.value)}
                                                    data-testid={`partner-shop-branch-hours-${idx}`}
                                                    placeholder="Mon-Sat 9am-9pm"
                                                />
                                            </FormRow>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                </div>

                {/* Footer actions */}
                <div className="sticky bottom-0 -mx-6 mt-6 border-t border-slate-200 bg-white px-6 py-4">
                    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                        <Button
                            variant="outline"
                            onClick={onClose}
                            disabled={saving}
                            data-testid="partner-shop-cancel-btn"
                            className="rounded-xl border-slate-200"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={onSave}
                            disabled={!canSave || saving}
                            data-testid="partner-shop-save-btn"
                            className="btn-primary rounded-xl"
                        >
                            {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}
                            {isEdit ? "Save Changes" : "Create Brand"}
                        </Button>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
}

function FormRow({ label, hint, children, className = "" }) {
    return (
        <label className={`flex flex-col gap-1 ${className}`}>
            <span className="font-heading text-[11px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
                {label}
            </span>
            {children}
            {hint && (
                <span className="font-body text-[11px] text-[var(--text-muted)]">{hint}</span>
            )}
        </label>
    );
}

/**
 * Sync the branches array back to the server by diffing the previous and next
 * lists positionally. We PATCH indices that changed, POST any extras appended,
 * and DELETE any that were removed. This keeps the drawer's Save action a
 * single logical operation while still using the branch-scoped REST endpoints.
 */
async function syncBranches(shopId, previous, next) {
    const prev = previous || [];
    const nx = next || [];
    // Update / add pass — walk through the new list.
    for (let i = 0; i < nx.length; i++) {
        const nb = nx[i];
        if (i < prev.length) {
            // Same slot — always PATCH; the payload is cheap.
            await api.patch(`/admin/partner-shops/${shopId}/branches/${i}`, nb);
        } else {
            await api.post(`/admin/partner-shops/${shopId}/branches`, nb);
        }
    }
    // Delete leftovers — walk from the tail so indices remain valid.
    for (let i = prev.length - 1; i >= nx.length; i--) {
        await api.delete(`/admin/partner-shops/${shopId}/branches/${i}`);
    }
}
